import { AnimatePresence, motion, useMotionValue } from 'framer-motion';
import type React from 'react';
import { useState } from 'react';

import { STATUS_COLORS } from './config/status-config';

type Props = {
  progress: number;
  size?: number;
  stroke?: number;
  onClick?: () => void;
  itemId?: string;
  pageId?: string;
  disabled?: boolean;
  centerContent?: React.ReactNode;
  progressOverride?: number;
  isFocused?: boolean;
  onTimerClick?: () => void;
  statusColor?: string;
};

export const LoopProgressRing: React.FC<Props> = ({
  progress,
  size = 48,
  stroke = 4,
  onClick,
  disabled = false,
  centerContent,
  progressOverride,
  isFocused = false,
  onTimerClick,
  statusColor,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [undoAnimating, setUndoAnimating] = useState(false);
  const radius = (size - stroke) / 2 - 1;
  const circumference = 2 * Math.PI * radius;
  const ringProgress = typeof progressOverride === 'number' ? progressOverride : progress;
  const isComplete = progress >= 100;

  const pathLength = useMotionValue(isComplete ? 1 : progress / 100);

  const GREEN = STATUS_COLORS.DONE;
  const DEFAULT_STROKE = 'var(--affine-text-secondary-color)';

  // Checkmark path - clean icon-like proportions based on 24x24 viewBox
  const cx = size / 2;
  const cy = size / 2;
  const scale = size / 24;
  const checkPath = `M${cx - 4.5 * scale},${cy + 1 * scale} L${cx - 1.5 * scale},${cy + 4 * scale} L${cx + 5 * scale},${cy - 3.5 * scale}`;

  // Animation variants for the checkmark
  const checkVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { type: "spring", duration: 0.8, bounce: 0.2 },
        opacity: { duration: 0.2 }
      }
    },
    reverse: {
      pathLength: 0,
      opacity: 0,
      transition: {
        pathLength: { duration: 0.5, ease: 'easeInOut' },
        opacity: { duration: 0.2, delay: 0.3 }
      }
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isFocused && onTimerClick) {
      onTimerClick();
      return;
    }

    if (disabled || !onClick) return;

    if (isComplete) {
      // Play a pleasant undo animation (shrink green pulse + reverse check)
      setUndoAnimating(true);
      // Trigger the callback after the reverse animation
      setTimeout(() => {
        onClick();
      }, 450);
      // Allow a short tail for the fade-out before clearing local flag
      setTimeout(() => setUndoAnimating(false), 800);
      return;
    }

    setIsAnimating(true);

    // Animate the path length for a smooth transition
    const controls = {
      start: pathLength.get(),
      end: 1
    };

    // Animate the path length
    const startTime = Date.now();
    const duration = 650;

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(1, elapsed / duration);

      // Use a custom easing function for a more natural feel
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      pathLength.set(controls.start + (controls.end - controls.start) * eased);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          onClick();
        }, 200);
      }
    };

    requestAnimationFrame(tick);
  };

  return (
    <motion.div
      initial={false}
      animate={{
        scale: isComplete ? 1.05 : 1,
        rotate: 0
      }}
      whileHover={{
        scale: disabled ? 1 : 1.08,
        boxShadow: disabled
          ? 'none'
          : (isComplete && !undoAnimating)
            ? `0 0 0 2px ${GREEN}1A`
            : 'none'
      }}
      transition={{
        type: 'spring',
        stiffness: 260,
        damping: 20
      }}
      whileTap={{ scale: 0.96 }}
      onClick={handleClick}
      style={{
        width: size,
        height: size,
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'box-shadow 0.2s ease',
        overflow: 'visible',
      }}
    >
      <svg width={size} height={size}>
        {/* Base Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="var(--affine-text-secondary-color)"
          strokeWidth={stroke * 0.5}
          fill="none"
          opacity={0.3}
        />

        {/* Hint checkmark - shows when not complete */}
        {!isComplete && !isAnimating && (
          <path
            d={checkPath}
            stroke="var(--affine-text-secondary-color)"
            strokeWidth={stroke * 0.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity={0.4}
          />
        )}

        {/* Progress Circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={statusColor || DEFAULT_STROKE}
          strokeWidth={stroke * 0.75}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (circumference * ringProgress) / 100}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          initial={{ stroke: statusColor || DEFAULT_STROKE }}
          animate={{
            stroke: (isComplete && !undoAnimating) || isAnimating ? GREEN : (statusColor || DEFAULT_STROKE)
          }}
          transition={{
            duration: 0.5,
            ease: 'easeInOut'
          }}
        />

      </svg>

      {!isComplete && !isAnimating && centerContent && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          zIndex: 10
        }}>
          {centerContent}
        </div>
      )}

      {/* Completion Indicator - show for complete, completing, or undoing */}
      <AnimatePresence>
        {(isComplete || isAnimating || undoAnimating) && (
          <svg
            width={size}
            height={size}
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            <motion.circle
              key="completion-background"
              cx={size / 2}
              cy={size / 2}
              r={radius * 0.6}
              initial={{ scale: (isAnimating || undoAnimating) ? 0 : 1, opacity: (isAnimating || undoAnimating) ? 0 : 1 }}
              animate={{
                scale: isAnimating ? [0, 1.1, 1] : (undoAnimating ? [1, 0.9, 0] : 1),
                opacity: isAnimating ? [0, 0.8, 1] : (undoAnimating ? [1, 0.6, 0] : 1),
                fill: GREEN
              }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{
                duration: isAnimating ? 0.4 : (undoAnimating ? 0.45 : 0.3),
                delay: isAnimating ? 0.3 : 0,
                times: isAnimating ? [0, 0.7, 1] : (undoAnimating ? [0, 0.6, 1] : [0, 1]),
                ease: isAnimating ? 'backOut' : (undoAnimating ? 'backIn' : 'easeInOut')
              }}
              fillOpacity={0.2}
            />

            {/* Animated Checkmark */}
            <motion.path
              key="checkmark"
              d={checkPath}
              stroke={GREEN}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              initial={undoAnimating ? 'visible' : 'hidden'}
              animate={undoAnimating ? 'reverse' : ((isComplete || isAnimating) ? 'visible' : 'hidden')}
              variants={checkVariants}
              custom={1}
              style={{
                filter: `drop-shadow(0px 1px 2px ${STATUS_COLORS.DONE}80)`,
              }}
            />
          </svg>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
